import React, { useRef, useState } from 'react'
import { useAudioMarkerEngine } from './useAudioMarkerEngine'
import WaveformPlot from '@/features/services/media/components/WaveformPlot'
import MarkerTimeline from './MarkerTimeline'
import AudioControlBar from './AudioControlBar'
import StemRowsPanel from './StemRowsPanel'
import YoutubeIngestSection from './YoutubeIngestSection'
import AudioLibraryPicker from './AudioLibraryPicker'

type SourceMode = 'file' | 'youtube' | 'library'

/**
 * AudioAnalyzerPanel - SPA for audio analysis and marker-based looping
 *
 * Three source modes:
 *   - File upload: local audio file decoded via Web Audio API
 *   - YouTube: ingest pipeline → opus artifact stored in GCS
 *   - Library: pick any completed track from the audio library
 *
 * DRY design:
 * - Reuses WaveformPlot, AudioStemRow from Music Production
 * - Modular child components (MarkerTimeline, AudioControlBar, StemRowsPanel)
 * - Single engine hook owns all audio state
 */
export default function AudioAnalyzerPanel() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const engine = useAudioMarkerEngine()
  const [sourceMode, setSourceMode] = useState<SourceMode>('library')
  const [refreshKey, setRefreshKey] = useState(0) // bumped after ingest completes

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) await engine.loadAudioFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const hasAudio = engine.duration > 0

  const TAB_CLASSES = (active: boolean) =>
    `px-3 py-1.5 text-xs font-medium rounded transition-colors ${
      active
        ? 'bg-purple-600 text-white'
        : 'pac-glass-btn text-neutral-400 hover:text-neutral-200'
    }`

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {/* Source panel */}
      <div className="shrink-0 pac-glass rounded-xl border border-neutral-700/40 p-4 space-y-4">
        {/* Mode tabs */}
        <div className="flex gap-2">
          <button className={TAB_CLASSES(sourceMode === 'file')} onClick={() => setSourceMode('file')}>
            Upload File
          </button>
          <button className={TAB_CLASSES(sourceMode === 'youtube')} onClick={() => setSourceMode('youtube')}>
            YouTube
          </button>
          <button className={TAB_CLASSES(sourceMode === 'library')} onClick={() => setSourceMode('library')}>
            Library
          </button>
        </div>

        {/* File upload */}
        {sourceMode === 'file' && (
          <>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center justify-center px-4 py-6 rounded border-2 border-dashed border-neutral-700 bg-neutral-900/40 hover:border-purple-500/50 cursor-pointer transition-colors"
            >
              <p className="text-sm text-neutral-400 text-center">
                <span className="text-neutral-300 font-medium">Click to select audio file</span>
                <br />
                <span className="text-xs text-neutral-600">MP3, WAV, AAC, OGG, Opus</span>
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileSelect}
              className="hidden"
              aria-label="Upload audio file"
            />
          </>
        )}

        {/* YouTube ingest */}
        {sourceMode === 'youtube' && (
          <YoutubeIngestSection
            onIngested={() => {
              setRefreshKey((k) => k + 1)
              setSourceMode('library')
            }}
          />
        )}

        {/* Library picker */}
        {sourceMode === 'library' && (
          <AudioLibraryPicker
            key={refreshKey}
          />
        )}

        {/* Loading / error banners */}
        {engine.isLoading && (
          <p className="text-xs text-neutral-400">Loading audio…</p>
        )}
        {engine.error && (
          <p className="text-xs text-rose-400">{engine.error}</p>
        )}
      </div>

      {/* Player section — shown once audio is loaded */}
      {hasAudio && (
        <>
          {/* Waveform */}
          <div className="shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                Waveform
              </h3>
              <span className="text-xs text-neutral-500 font-mono">
                {formatTime(engine.currentTime)} / {formatTime(engine.duration)}
              </span>
            </div>
            <div className="bg-neutral-950/50 rounded border border-neutral-800 p-1 overflow-hidden">
              <WaveformPlot
                analyser={engine.analyser}
                height={120}
                status={engine.isPlaying ? 'playing' : 'paused'}
                color="#a78bfa"
                background="var(--neutral-950, #09090b)"
                showGrid={true}
              />
            </div>
          </div>

          {/* Playback controls */}
          <AudioControlBar engine={engine} />

          {/* Stem rows */}
          <StemRowsPanel engine={engine} />

          {/* Markers & Loop timeline */}
          <MarkerTimeline engine={engine} />
        </>
      )}
    </div>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 100)
  return `${mins}:${String(secs).padStart(2, '0')}.${String(ms).padStart(2, '0')}`
}

